import type { ReactElement } from 'react';
import { api } from '../shared/api/endpoints';
import { useMessages } from '../shared/i18n/locale';
import { formatTime, minutesUntil } from '../shared/lib/format';
import { useAsync } from '../shared/lib/use-async';
import { mergePositions, useLiveSocket } from '../shared/lib/use-live-socket';
import { Card, DriverChip, EmptyState, Field, LiveBadge } from '../shared/ui/primitives';

/**
 * Live-карта (разд. 7, FR-573).
 *
 * **Карты здесь пока нет**, и это осознанно: карта — Яндекс Maps JS API, ключ к
 * которому не получен (Б-01 журнала незакрытого), а рисовать подложку другого
 * поставщика запрещает правило одного поставщика (A12). До ключа экран показывает то
 * же содержимое списком — позиции, возраст координаты, следующую точку и ETA.
 *
 * Возраст позиции подписан всегда: без него простой не отличить от потери связи, и
 * диспетчер начнёт доверять устаревшим точкам (FR-606).
 *
 * Данные приходят двумя путями сразу: опрос снапшота раз в десять секунд и подписка на
 * `/ws/live`. Опрос не отключается при живом сокете намеренно — сокет транспорт, а не
 * источник истины (разд. 7 брифа), и экран обязан работать при его потере. Значок
 * состояния показывает, какой путь сейчас работает.
 */

const REFRESH_MS = 10_000;

export function LiveMapPage(): ReactElement {
  const messages = useMessages();
  const snapshot = useAsync(() => api.liveSnapshot(), [], { intervalMs: REFRESH_MS });
  const live = useLiveSocket();

  const drivers = mergePositions(snapshot.data?.drivers ?? [], live.positions);
  const generatedAt = snapshot.data?.generated_at ?? null;

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-4)' }}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>{messages.cabinet.nav.live_map}</h1>
        <LiveBadge
          state={
            live.state === 'connected'
              ? 'connected'
              : snapshot.error === null
                ? 'polling'
                : 'reconnecting'
          }
          updatedSecondsAgo={secondsSince(generatedAt)}
        />
      </header>

      <div
        style={{
          padding: 'var(--sp-3)',
          borderRadius: 'var(--r-md)',
          background: 'var(--warn-weak)',
          color: 'var(--warn)',
        }}
      >
        {messages.cabinet.live.mapUnavailable}
      </div>

      {drivers.length === 0 ? (
        <EmptyState text={messages.cabinet.common.empty} />
      ) : (
        drivers.map((driver) => (
          <Card
            key={driver.shift_id ?? driver.driver_id}
            tone={driver.is_stale === true ? 'danger' : undefined}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--sp-3)' }}>
              <DriverChip name={driver.driver_name ?? null} stale={driver.is_stale === true} />
              <span
                className="num"
                style={{ color: driver.is_stale === true ? 'var(--danger)' : 'var(--text-muted)' }}
              >
                {`${messages.cabinet.live.updatedAgo} ${formatTime(driver.recorded_at)}`}
              </span>
            </div>

            <div
              style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'var(--sp-3)' }}
            >
              <Field
                label={messages.task.etaNext}
                value={<span className="num">{formatTime(driver.next_stop_eta_at)}</span>}
              />
              <Field
                label={messages.task.etaComplete}
                value={<span className="num">{formatTime(driver.eta_complete_at)}</span>}
              />
              <Field
                label={messages.task.etaReturn}
                value={<span className="num">{formatTime(driver.eta_return_at)}</span>}
              />
              <Field
                label={messages.report.stops}
                value={<span className="num">{driver.stops_remaining ?? 0}</span>}
              />
            </div>

            {driver.is_stale === true ? (
              <span style={{ color: 'var(--danger)' }}>{messages.cabinet.live.staleWarning}</span>
            ) : null}
          </Card>
        ))
      )}
    </div>
  );
}

function secondsSince(iso: string | null): number | null {
  const minutes = minutesUntil(iso);
  return minutes === null ? null : Math.abs(minutes) * SECONDS_IN_MINUTE;
}

const SECONDS_IN_MINUTE = 60;
